import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { checkOf, pageOf, useCourseAddress } from '@/shared/lib/address';

import { ReportButton } from '@/features/moderation/ReportButton';
import { RichText } from '@/music/text/RichText';
import { GuestNotice } from '@/features/auth/GuestNotice';
import { useMusicFormatter } from '@/music/i18n/MusicI18n';
import { useGetCourseProgressQuery, useGetCourseQuery } from '@/shared/api/endpoints';
import { errorStatus } from '@/shared/api/errors';
import type { LessonProgress, LessonSummary, TopicOut, TopicProgress } from '@/shared/api/types';
import { cn } from '@/shared/lib/cn';
import { useContentLocale } from '@/shared/lib/locale';
import { usePageTitle } from '@/shared/lib/usePageTitle';
import { Alert, Badge, Card, PageTitle, Spinner } from '@/shared/ui/primitives';

const percent = (score: number) => `${Math.round(score * 100)}%`;

function StatusBadge({ status, unlocked }: { status: LessonProgress['status']; unlocked: boolean }) {
  const { t } = useTranslation();
  const key = unlocked ? status : 'locked';
  return (
    <Badge
      tone={key === 'completed' ? 'ok' : key === 'in_progress' ? 'info' : 'neutral'}
      icon={key === 'locked' ? 'lock' : key === 'completed' ? 'check-circle' : undefined}
      className="shrink-0"
    >
      {t(`status.${key}`)}
    </Badge>
  );
}

interface RowProps {
  to: string;
  unlocked: boolean;
  status: LessonProgress['status'];
  title: string;
  description: string;
  meta: string;
}

/** A lesson or check in the course map: a link when open, plain text when locked. */
function Row({ to, unlocked, status, title, description, meta }: RowProps) {
  const body = (
    <>
      <div className="min-w-0">
        <p className="font-semibold">
          <RichText text={title} hints="hover" />
        </p>
        {description ? (
          <p className="mt-0.5 text-sm text-muted">
            <RichText text={description} hints="hover" />
          </p>
        ) : null}
        <p className="mt-1 text-xs text-muted">{meta}</p>
      </div>
      <StatusBadge status={status} unlocked={unlocked} />
    </>
  );
  const layout = 'flex items-start justify-between gap-4 rounded-xl border border-line p-4';
  if (!unlocked) {
    return (
      <div className={cn(layout, 'bg-sunken opacity-70')} aria-disabled>
        {body}
      </div>
    );
  }
  return (
    <Link to={to} className={cn(layout, 'bg-surface transition-colors hover:border-brand')}>
      {body}
    </Link>
  );
}

function LessonRow({ lesson, progress }: { lesson: LessonSummary; progress?: LessonProgress }) {
  const { t } = useTranslation();
  const best = progress?.bestScore;
  return (
    <Row
      to={pageOf(lesson.id)}
      unlocked={progress?.unlocked ?? false}
      status={progress?.status ?? 'not_started'}
      title={lesson.title}
      description={lesson.description ?? ''}
      meta={[
        best == null ? null : t('course.best', { score: percent(best) }),
      ]
        .filter(Boolean)
        .join(' · ')}
    />
  );
}

function TopicSection({
  topic,
  progress,
  lessons,
}: {
  topic: TopicOut;
  progress?: TopicProgress;
  lessons: Record<string, LessonProgress>;
}) {
  const { t } = useTranslation();
  const best = progress?.bestScore;
  return (
    <Card>
      <h2 className="text-xl font-bold">
        <RichText text={topic.title} hints />
      </h2>
      {topic.description ? (
        <p className="mt-1 text-muted">
          <RichText text={topic.description} hints />
        </p>
      ) : null}
      <ol className="mt-5 space-y-3">
        {topic.lessons.map((lesson) => (
          <li key={lesson.id}>
            <LessonRow lesson={lesson} progress={lessons[lesson.id]} />
          </li>
        ))}
        <li>
          <Row
            to={checkOf(topic.id)}
            unlocked={progress?.checkUnlocked ?? false}
            status={progress?.status === 'completed' ? 'completed' : 'not_started'}
            title={t('course.check')}
            description={t('course.checkDescription')}
            meta={[
              t('course.questions', { count: topic.check.itemCount }),
              best == null ? null : t('course.best', { score: percent(best) }),
            ]
              .filter(Boolean)
              .join(' · ')}
          />
        </li>
      </ol>
    </Card>
  );
}

function BackToHub() {
  const { t } = useTranslation();
  return (
    <Link to="/" className="mb-4 inline-block text-sm text-brand hover:underline">
      ← {t('nav.hub')}
    </Link>
  );
}

/** `/:author/:course`: a course at a glance: topics, lessons, what is done and what is open. */
export function CourseMapPage() {
  const { t } = useTranslation();
  const courseId = useCourseAddress();
  const lang = useContentLocale();
  const course = useGetCourseQuery({ id: courseId, lang });
  const progress = useGetCourseProgressQuery(courseId);
  const formatter = useMusicFormatter();
  usePageTitle(course.data ? formatter.text(course.data.title) : null);

  if (course.isError || progress.isError) {
    // A course one may not see answers exactly like one that does not exist.
    // So does an address that is not shaped like one (422): this route takes any two
    // words, and "/Foo/Bar" is no more a course than one that was never made.
    const missing = [404, 422].includes(errorStatus(course.error ?? progress.error) ?? 0);
    return (
      <>
        <BackToHub />
        <Alert tone="bad">{t(missing ? 'errors.course_not_found' : 'errors.loadFailed')}</Alert>
      </>
    );
  }
  if (!course.data || !progress.data) return <Spinner label={t('loading')} />;

  const { author } = course.data;
  return (
    <>
      <BackToHub />
      <PageTitle subtitle={author ? t('hub.by', { name: author.name }) : null}>
        <RichText text={course.data.title} hints />
      </PageTitle>
      <ReportButton kind="course" id={courseId} className="mb-6" />
      <GuestNotice className="mb-6" />
      <div className="space-y-6">
        {course.data.topics.map((topic) => (
          <TopicSection
            key={topic.id}
            topic={topic}
            progress={progress.data?.topics[topic.id]}
            lessons={progress.data?.lessons ?? {}}
          />
        ))}
      </div>
    </>
  );
}
